import { RetornoListarDecisoes } from "@/types/processo-eletronico/decisoes/retorno-listar-decisoes";
import { Service } from "../mainService";
import { UnidadeGestoraResumida } from "@/types/ug/unidade-gestora-resumida";
import { PortalError } from "@/types/portal-error";
import { FiltrosDecisoes } from "@/types/processo-eletronico/decisoes/filtros-decisoes";

const protocoloService = {
  listarTiposProcessos,
  listarTiposProtocolos,
  ObterTipoDocumentoRespAComunicacaoProcessual,
  getDecisoes,
  filtrarUgs,
  getRascunhoProtocolo,
  obterResponsavelUg,
  obterResponsavelUgCP,
  listarTiposAto,
  listarTiposMultimidia,
  listarTiposRespostaCP,
  downloadArquivoRascunho,
  downloadPecaUnicaPdf,
  downloadAtoPdf,
  downloadMidiaPecaUnicaPdf,
  removerArquivo,
  uploadArquivo,
  salvarRascunho,
  removerRascunhoProtocolo,
  assinarEnviarProtocolo,
  iniciarAssinaturaToken,
  finalizarAssinaturaToken,
  listarRecibosDoUsuario,
  listarComunicacoesEletronicasUsuario,
  listarComunicacoesEletronicasUsuarioUG,
  visualizarNotificacao,
  carregarProcessosAdvogado,
  carregarProcessosProtocoloAdmin,
  carregarProcessosProtocoloInteressado,
  carregarProcessosProtocoloUG,
  carregarDetalheProtocoloProcesso,
  getDetalheProtocoloProcesso
};

async function listarTiposProcessos() {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/listarTiposProcessos"
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function listarTiposProtocolos() {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/listarTiposProtocolos"
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

/**
 * Busca todos os tipos de documentos públicos, filtra e retorna apenas o que é utilizado em resposta a comunicações processuais.
 * @since 20220505
 * @returns TipoDocumento Resposta a Comunicacao Processual
 */
async function ObterTipoDocumentoRespAComunicacaoProcessual() {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/listarTiposProcessos"
    );
    if (response && response.status === 200) {
      return response.data.find(
        (el) => el.Descricao === "RESPOSTA A COMUNICAÇÃO PROCESSUAL"
      );
    }
    return null;
  } catch (e: any) {
    return e.response;
  }
}

async function getRascunhoProtocolo(data) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/getRascunhoProtocolo",
      data
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function obterResponsavelUg(codUg) {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/obterResponsavelUg/" +
        codUg
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function obterResponsavelUgCP(codUg) {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API +
        "/comunicacoesProcessuais/obterResponsavelUg/" +
        codUg
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function listarTiposAto(tpDocumento) {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/listarTiposAto/" +
        tpDocumento
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function listarTiposMultimidia(tpDocumento) {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/listarMultimidiasSuportados/" +
        tpDocumento
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function listarTiposRespostaCP() {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API +
        "/comunicacoesProcessuais/listarTiposRespostaCP"
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function downloadArquivoRascunho(idRascunho, idArquivo) {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/downloadArquivoRascunho/" +
        idRascunho +
        "/" +
        idArquivo,
      { responseType: "blob" }
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function downloadPecaUnicaPdf(
  codProtocoloCriptografado: string,
  carimbo = "S"
): Promise<Blob> {
  try {
    const response = await Service.$http.get(
      (process.env.VUE_APP_PDF_API || "") + "/api/pdf",
      {
        params: { c: codProtocoloCriptografado, carimbo },
        responseType: "arraybuffer"
      }
    );
    const contentType = response.headers["content-type"] || "";

    if (
      response.status === 200 &&
      response.data &&
      (contentType.includes("application/pdf") ||
        contentType.includes("image") ||
        contentType.includes("octet-stream"))
    ) {
      return new Blob([response.data], { type: contentType });
    }

    throw new PortalError("Erro ao baixar a peça única.");
  } catch (e: any) {
    if ([400, 401, 403].includes(e.response?.status) && e.response?.data) {
      try {
        const mensagem = new TextDecoder().decode(e.response.data);
        const erro = JSON.parse(mensagem);
        throw new PortalError(
          erro.erros || erro.message || erro.msg || "Erro ao baixar a peça única."
        );
      } catch (erroDeParse) {
        if (erroDeParse instanceof PortalError) throw erroDeParse;
      }
    }

    throw e;
  }
}

async function downloadAtoPdf(
  codArquivoDigitalCriptografado: string
): Promise<Blob> {
  try {
    const response = await Service.$http.get(
      (process.env.VUE_APP_PDF_API || "") + "/api/pdf/autentica",
      {
        params: { c: codArquivoDigitalCriptografado },
        responseType: "arraybuffer"
      }
    );
    const contentType = response.headers["content-type"] || "";

    if (
      response.status === 200 &&
      response.data &&
      (contentType.includes("application/pdf") ||
        contentType.includes("image") ||
        contentType.includes("octet-stream"))
    ) {
      return new Blob([response.data], { type: contentType });
    }

    throw new PortalError("Erro ao baixar o ato.");
  } catch (e: any) {
    if ([400, 401, 403].includes(e.response?.status) && e.response?.data) {
      try {
        const mensagem = new TextDecoder().decode(e.response.data);
        const erro = JSON.parse(mensagem);
        throw new PortalError(erro.erros || erro.message || erro.msg || "Erro ao baixar o ato.");
      } catch (erroDeParse) {
        if (erroDeParse instanceof PortalError) throw erroDeParse;
      }
    }

    throw e;
  }
}

async function downloadMidiaPecaUnicaPdf(
  codCriptografadoMidia: string
): Promise<Blob> {
  const response = await Service.$http.get(
    (process.env.VUE_APP_PDF_API || "") + "/api/pdf/media",
    {
      params: { c: codCriptografadoMidia },
      responseType: "blob"
    }
  );
  if (!response || response.status !== 200 || !response.data) {
    console.error(response);
    throw new PortalError("Erro ao baixar a multimídia.");
  }
  return response.data;
}

async function removerArquivo(data) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/removeArquivoRascunho",
      data
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function uploadArquivo(data) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/uploadArquivoRascunho",
      data
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function salvarRascunho(data) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/salvarRascunhoProtocolo",
      data
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function removerRascunhoProtocolo(data) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/removeRascunhoProtocolo",
      data
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function assinarEnviarProtocolo(data) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/assinarEnviarProtocolo",
      data
      /*{
                headers: {
                "content-type": "multipart/form-data",
                },
            }*/
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function iniciarAssinaturaToken(data) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/iniciarAssinaturaToken",
      data
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function finalizarAssinaturaToken(data) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/finalizarAssinaturaToken",
      data,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function listarRecibosDoUsuario() {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/listarRecibosUsuario"
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function listarComunicacoesEletronicasUsuarioUG() {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API +
        "/comunicacoesProcessuais/consultarNotificacoesUsuarioUG"
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function listarComunicacoesEletronicasUsuario() {
  try {
    const response = await Service.$http.get(
      process.env.VUE_APP_MESSAGES_API +
        "/comunicacoesProcessuais/consultarNotificacoesUsuario"
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function visualizarNotificacao(data) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API +
        "/comunicacoesProcessuais/visualizarNotificacao",
      data
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function carregarProcessosProtocoloUG(data) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/consultarProtocolosUG",
      data
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function carregarProcessosProtocoloInteressado(data) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/consultarProtocolosInteressado",
      data
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function carregarProcessosProtocoloAdmin(data) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/consultarProtocolosAdm",
      data
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function carregarProcessosAdvogado(data) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/carregarProcessosAdvogado",
      data
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function carregarDetalheProtocoloProcesso(data) {
  try {
    const response = await Service.$http.post(
      process.env.VUE_APP_MESSAGES_API +
        "/processosEletronicos/verDetalhesProtocolo",
      data
    );
    return response;
  } catch (e: any) {
    return e.response;
  }
}

async function getDetalheProtocoloProcesso(codProtocolo: number) {

  const response = await Service.$http.get(
    `${process.env.VUE_APP_MESSAGES_API}/processosEletronicos/obterProtocolo/${codProtocolo}`
  )
  if (response && response.status === 200) return response.data
  else throw new PortalError('Erro ao obter detalhes do protocolo/processo.')
}



async function getDecisoes(
  filtros: FiltrosDecisoes
): Promise<RetornoListarDecisoes> {
  const response = await Service.$http.get(
    `${process.env.VUE_APP_MESSAGES_API}/Decisoes${filtros.toQueryString()}`
    //`${process.env.VUE_APP_MESSAGES_API}/Decisoes`
  );
  //console.log(response);
  if (response && response.status === 200)
    return new RetornoListarDecisoes(response.data);
  console.error("Falha ao buscar decisões", response);
  throw new Error(response.msg ?? "Falha ao buscar decisões");
}

/**
 * Remova-me após o merge com avisos-2024
 * @param nome
 * @param municipio
 * @param esfera
 * @param tipoUg
 * @param areaCI
 * @returns
 */
async function filtrarUgs(
  nome: string | null = null,
  municipio: string | null = null,
  esfera: string | null = null,
  tipoUg: string | null = null,
  areaCI: string | null = null
): Promise<UnidadeGestoraResumida[]> {
  const data = new FormData();
  if (nome) data.append("nome", nome);
  if (municipio) data.append("municipio", municipio);
  if (esfera) data.append("esfera", esfera);
  if (tipoUg) data.append("tipoUg", tipoUg);
  if (areaCI) data.append("areaCI", areaCI);

  const response = await Service.$http.post(
    process.env.VUE_APP_MESSAGES_API + "/avisos/filtrarUgs",
    data,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  if (!response || response.status !== 200) {
    console.error(response);
    throw new PortalError("Erro ao filtrar UGs");
  }
  return response.data
    .filter((f) => f.Id && f.Nome)
    .map((r) => new UnidadeGestoraResumida(r));
}

export default protocoloService;
